import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Quote, BadgeCheck, MapPin, Clock, Dumbbell, Flame, Zap, HeartPulse, ShieldCheck, Eye, Award, Store, Crown, ArrowRight, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTier } from "@/context/TierContext";
import { useStoreInfo } from "@/context/StoreInfoContext";
import { Stars } from "@/components/Stars";

const SectionHead = ({ overline, title, right }) => (
  <div className="flex items-end justify-between gap-6 mb-10">
    <div>
      <div className="font-overline text-xs text-[#D4AF37] mb-3">— {overline}</div>
      <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">{title}</h2>
    </div>
    {right}
  </div>
);

/* ---------- TROVA LA TUA ROUTINE ---------- */
const ROUTINES = [
  { id: "massa", title: "MASSA MUSCOLARE", desc: "Proteine, carboidrati e creatina per costruire volume.", icon: Dumbbell, to: "/catalog?cat=proteine", accent: "from-[#D4AF37]/30" },
  { id: "definizione", title: "DEFINIZIONE", desc: "Proteine isolate, termogenici e controllo del peso.", icon: Flame, to: "/catalog?cat=perdita-peso", accent: "from-orange-500/25" },
  { id: "performance", title: "PERFORMANCE", desc: "Pre-workout, aminoacidi ed endurance per spingere di più.", icon: Zap, to: "/catalog?cat=pre-workout", accent: "from-sky-500/25" },
  { id: "benessere", title: "BENESSERE", desc: "Vitamine, omega e salute quotidiana.", icon: HeartPulse, to: "/catalog?cat=salute-benessere", accent: "from-emerald-500/25" },
];

export const RoutineSection = () => (
  <section className="py-20 max-w-7xl mx-auto px-6" data-testid="routine-section">
    <SectionHead overline="PER OBIETTIVO" title={<>TROVA LA TUA<br />ROUTINE</>} />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {ROUTINES.map((r) => (
        <Link key={r.id} to={r.to} className={`group relative overflow-hidden border border-white/10 bg-gradient-to-br ${r.accent} to-[#0A0A0A] p-7 min-h-[220px] flex flex-col justify-between hover:border-[#D4AF37] transition-colors`} data-testid={`routine-${r.id}`}>
          <r.icon className="w-9 h-9 text-[#D4AF37]" strokeWidth={1.5} />
          <div>
            <div className="font-display font-black text-2xl leading-none mb-2">{r.title}</div>
            <div className="text-sm text-neutral-400">{r.desc}</div>
            <div className="font-overline text-[10px] text-[#D4AF37] mt-4 flex items-center gap-1 group-hover:gap-2 transition-all">Scopri <ArrowRight className="w-3 h-3" /></div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

/* ---------- PERCHÉ POWER FITNESS NUTRITION ---------- */
const WHY = [
  { icon: ShieldCheck, title: "SELEZIONE", desc: "Selezioniamo brand e prodotti sulla base di ingredienti, formulazioni, valori nutrizionali e affidabilità." },
  { icon: Eye, title: "TRASPARENZA", desc: "Etichette chiare, prezzi onesti, nessun costo nascosto. Ti diciamo cosa serve davvero." },
  { icon: Award, title: "ESPERIENZA", desc: "Anni dietro il bancone e in palestra: consigli su misura per il tuo obiettivo." },
  { icon: Store, title: "ONLINE + NEGOZIO", desc: "Ordina in app, ritira a San Cataldo o Canicattì, oppure ricevi a casa in 2-4 giorni." },
];

export const WhySection = () => (
  <section className="py-20 border-y border-white/10 bg-black" data-testid="why-section">
    <div className="max-w-7xl mx-auto px-6">
      <SectionHead overline="LA DIFFERENZA" title="PERCHÉ POWER FITNESS NUTRITION?" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {WHY.map((w, i) => (
          <div key={w.title} className="bg-[#0A0A0A] p-8 group hover:bg-[#141414] transition-colors" data-testid={`why-${i}`}>
            <div className="font-display font-black text-5xl text-white/10 mb-4 group-hover:text-[#D4AF37]/30 transition-colors">0{i + 1}</div>
            <w.icon className="w-7 h-7 text-[#D4AF37] mb-4" strokeWidth={1.5} />
            <div className="font-display font-bold text-xl mb-2">{w.title}</div>
            <div className="text-sm text-neutral-400 leading-relaxed">{w.desc}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- PUNTI VENDITA ---------- */
export const StoresSection = () => {
  const { info } = useStoreInfo();
  const stores = info?.stores || [];
  return (
    <section className="py-20 max-w-7xl mx-auto px-6" data-testid="stores-section">
      <SectionHead overline="VIENI A TROVARCI" title={<>DUE NEGOZI,<br />UNA SQUADRA</>} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stores.map((s) => (
          <div key={s.id} className="relative border border-white/10 bg-[#141414] p-8 overflow-hidden group" data-testid={`store-card-${s.id}`}>
            <div className="absolute -right-6 -top-6 font-display font-black text-[120px] leading-none text-white/[0.04] select-none">{s.label.slice(0, 2).toUpperCase()}</div>
            <div className="relative">
              <div className="font-overline text-[10px] text-[#D4AF37] mb-2">PUNTO VENDITA</div>
              <div className="font-display font-black text-3xl mb-4">{s.label.toUpperCase()}</div>
              <div className="flex items-start gap-2 text-sm text-neutral-300 mb-2"><MapPin className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />{s.address}</div>
              {s.hours && <div className="flex items-start gap-2 text-sm text-neutral-400 mb-2"><Clock className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />{s.hours}</div>}
              <div className="flex flex-wrap gap-3 mt-6">
                <Button asChild className="bg-[#D4AF37] hover:bg-[#E5C158] text-black rounded-none font-overline text-[10px] h-10 px-5"><Link to="/reservations">Prenota il ritiro</Link></Button>
                <Button asChild variant="outline" className="border-white/20 bg-transparent hover:bg-white hover:text-black rounded-none font-overline text-[10px] h-10 px-5"><a href={`https://maps.google.com/?q=${encodeURIComponent(s.address)}`} target="_blank" rel="noreferrer">Indicazioni</a></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- PROGRAMMA FEDELTÀ ---------- */
const TIERS = [
  { id: "bronze", name: "BRONZE", perk: "Inizia a guadagnare punti", sub: "Gratis alla registrazione · 1 punto per ogni € · Ruota della Fortuna ogni giorno", color: "text-[#CD7F32]", border: "border-[#CD7F32]/40" },
  { id: "silver", name: "SILVER", perk: "Vantaggi riservati ai soci", sub: "Tessera €4,99/anno · Prezzo soci su tutto il catalogo · Personal Shopper dedicato", color: "text-[#C0C0C0]", border: "border-[#C0C0C0]/40" },
  { id: "gold", name: "GOLD", perk: "Il premio per i più fedeli", sub: "Si sblocca da sola a 1.500 punti nell'anno · Il massimo dei vantaggi soci, in regalo", color: "text-[#D4AF37]", border: "border-[#D4AF37]/60" },
];

export const LoyaltySection = () => {
  const { user } = useAuth();
  const { tier } = useTier();
  const [pts, setPts] = useState(null);
  useEffect(() => {
    if (!user) return setPts(null);
    api.get("/me/points").then(({ data }) => setPts(data)).catch(() => {});
  }, [user]);
  const pointsYear = pts?.points_year || 0;
  const pct = Math.min(100, Math.round((pointsYear / 1500) * 100));

  return (
    <section className="py-20 bg-black border-y border-white/10" data-testid="loyalty-section">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHead overline="PROGRAMMA FEDELTÀ" title={<>OGNI ACQUISTO<br />TI AVVICINA AL GOLD</>} right={!user && (
          <Button asChild className="hidden sm:inline-flex bg-[#D4AF37] hover:bg-[#E5C158] text-black rounded-none font-overline text-[10px] h-10 px-5" data-testid="loyalty-join"><Link to="/register">Iscriviti gratis</Link></Button>
        )} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {TIERS.map((t) => {
            const active = user && tier === t.id;
            return (
              <div key={t.id} className={`relative border ${active ? "border-[#D4AF37] bg-[#D4AF37]/5" : t.border + " bg-[#141414]"} p-7`} data-testid={`tier-card-${t.id}`}>
                {active && <div className="absolute top-3 right-3 font-overline text-[9px] bg-[#D4AF37] text-black px-2 py-0.5">IL TUO LIVELLO</div>}
                <Crown className={`w-7 h-7 ${t.color} mb-4`} strokeWidth={1.5} />
                <div className={`font-display font-black text-3xl ${t.color}`}>{t.name}</div>
                <div className="font-display font-bold text-xl mt-2">{t.perk}</div>
                <div className="text-xs text-neutral-400 mt-2 leading-relaxed">{t.sub}</div>
              </div>
            );
          })}
        </div>
        {user ? (
          <div className="border border-white/10 bg-[#141414] p-6" data-testid="loyalty-progress">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div>
                <div className="font-overline text-[10px] text-[#D4AF37]">I TUOI PUNTI {new Date().getFullYear()}</div>
                <div className="font-display font-black text-4xl">{pointsYear} <span className="text-base text-neutral-500 font-normal">/ 1.500 per la Gold</span></div>
              </div>
              <div className="text-sm text-neutral-400">{tier === "gold" ? "Sei Gold: grazie per la tua fedeltà 🎉" : `Ti mancano ${Math.max(0, 1500 - pointsYear)} punti per sbloccare la Gold`}</div>
            </div>
            <div className="h-3 bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#CD7F32] via-[#C0C0C0] to-[#D4AF37] transition-all duration-700" style={{ width: `${tier === "gold" ? 100 : pct}%` }} /></div>
            <div className="flex flex-wrap gap-3 mt-5">
              <Button asChild className="bg-[#D4AF37] hover:bg-[#E5C158] text-black rounded-none font-overline text-[10px] h-10 px-5"><Link to="/wheel"><Gift className="w-3.5 h-3.5 mr-1.5" /> Gira la ruota di oggi</Link></Button>
              <Button asChild variant="outline" className="border-white/20 bg-transparent hover:bg-white hover:text-black rounded-none font-overline text-[10px] h-10 px-5"><Link to="/profile">Vedi il mio profilo</Link></Button>
            </div>
          </div>
        ) : (
          <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="loyalty-cta">
            <div>
              <div className="font-display font-bold text-xl">Registrati gratis: sei subito Bronze.</div>
              <div className="text-sm text-neutral-400">Accumuli punti su ogni acquisto, giri la Ruota della Fortuna ogni giorno e giochi all'Arcade per vincere prodotti veri.</div>
            </div>
            <Button asChild className="bg-[#D4AF37] hover:bg-[#E5C158] text-black rounded-none font-overline text-[10px] h-11 px-6 shrink-0" data-testid="loyalty-register"><Link to="/register">Inizia a guadagnare punti <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></Link></Button>
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------- RECENSIONI ---------- */
export const ReviewsSection = () => {
  const { user } = useAuth();
  const { info } = useStoreInfo();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], avg: 0, count: 0 });
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const googleUrl = info?.google_place_url;
  const googleRating = Number(info?.google_rating) || 0;
  const googleCount = Number(info?.google_reviews_count) || 0;

  const load = () => api.get("/reviews").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (text.trim().length < 10) { toast.error("Scrivi almeno 10 caratteri"); return; }
    setSending(true);
    try {
      await api.post("/reviews", { rating, text: text.trim() });
      toast.success("Grazie! La tua recensione sarà pubblicata dopo la verifica.");
      setOpen(false); setText(""); setRating(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  return (
    <section className="py-20 max-w-7xl mx-auto px-6" data-testid="reviews-section">
      <SectionHead overline="DICONO DI NOI" title={<>RECENSIONI<br />DEI CLIENTI</>} right={(
        <div className="text-right hidden sm:block">
          {data.count > 0 && <div className="flex items-center justify-end gap-2 mb-1"><span className="font-display font-black text-3xl">{data.avg.toFixed(1)}</span><Stars n={Math.round(data.avg)} /></div>}
          <div className="text-xs text-neutral-500" data-testid="reviews-count">{data.count > 0 ? `${data.count} recensioni verificate` : "Sii il primo a recensirci"}</div>
        </div>
      )} />
      {(googleUrl || googleRating > 0) && (
        <div className="border border-white/10 bg-[#141414] p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="google-reviews-block">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white flex items-center justify-center font-black text-2xl shrink-0" style={{ color: "#4285F4" }}>G</div>
            <div>
              <div className="flex items-center gap-2">
                {googleRating > 0 && <span className="font-display font-black text-2xl">{googleRating.toFixed(1)}</span>}
                <Stars n={Math.round(googleRating || 5)} />
              </div>
              <div className="text-xs text-neutral-400">{googleCount > 0 ? `${googleCount} recensioni su Google` : "Le nostre recensioni su Google"}</div>
            </div>
          </div>
          {googleUrl && (
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" className="border-white/20 bg-transparent hover:bg-white hover:text-black rounded-none font-overline text-[10px] h-10 px-4"><a href={googleUrl} target="_blank" rel="noreferrer" data-testid="google-read-link">Leggi su Google</a></Button>
              <Button asChild className="bg-white text-black hover:bg-[#D4AF37] rounded-none font-overline text-[10px] h-10 px-4"><a href={googleUrl} target="_blank" rel="noreferrer" data-testid="google-write-link">Recensisci su Google</a></Button>
            </div>
          )}
        </div>
      )}
      {data.items.length === 0 ? (
        <div className="border border-dashed border-white/15 p-10 text-center text-sm text-neutral-500 mb-6" data-testid="reviews-empty">Nessuna recensione pubblicata ancora.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {data.items.map((r) => (
            <div key={r.review_id} className="border border-white/10 bg-[#141414] p-6 flex flex-col" data-testid={`review-${r.review_id}`}>
              <Quote className="w-6 h-6 text-[#D4AF37]/50 mb-3" />
              <Stars n={r.rating} />
              <p className="text-sm text-neutral-300 leading-relaxed mt-3 flex-1">"{r.text}"</p>
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-2">
                <div>
                  <div className="font-bold text-sm">{r.author}</div>
                  {r.product_name && <div className="text-[10px] text-neutral-500">su {r.product_name}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {r.source === "google" && <span className="flex items-center gap-1 text-[9px] font-overline text-neutral-300"><span className="font-black" style={{ color: "#4285F4" }}>G</span> via Google</span>}
                  {r.tier && r.tier !== "bronze" && <span className={`font-overline text-[9px] ${r.tier === "gold" ? "text-[#D4AF37]" : "text-[#C0C0C0]"}`}>{r.tier.toUpperCase()}</span>}
                  {r.verified && <span className="flex items-center gap-1 text-[9px] text-green-400 font-overline"><BadgeCheck className="w-3 h-3" /> Acquisto verificato</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Button onClick={() => (user ? setOpen(true) : navigate("/login"))} variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37] bg-transparent hover:bg-[#D4AF37] hover:text-black rounded-none font-overline text-[10px] h-11 px-6" data-testid="write-review-button">
        <Star className="w-3.5 h-3.5 mr-1.5" /> Lascia una recensione
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-[#D4AF37]/50 text-white max-w-md" data-testid="review-dialog">
          <DialogTitle className="font-display font-black text-2xl">LA TUA RECENSIONE</DialogTitle>
          <div className="space-y-4">
            <div><div className="font-overline text-[10px] text-neutral-400 mb-2">Valutazione</div><Stars n={rating} size="w-7 h-7" onPick={setRating} /></div>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={600} placeholder="Com'è andata con Power Fitness Nutrition? Prodotti, consigli, negozio..." className="bg-[#141414] border-white/20 rounded-none text-white text-sm" data-testid="review-text" />
            <div className="text-[10px] text-neutral-500">Le recensioni vengono verificate prima della pubblicazione. Pubblichiamo solo il tuo nome.</div>
            <Button onClick={submit} disabled={sending} className="w-full bg-[#D4AF37] hover:bg-[#E5C158] text-black rounded-none font-overline text-xs h-11" data-testid="review-submit">{sending ? "..." : "Invia recensione"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};


/* ---------- ACQUISTA ONLINE ---------- */
export const ShopOnlineSection = () => (
  <section className="relative bg-[#D4AF37] text-black py-16 md:py-20 overflow-hidden" data-testid="shop-online-section">
    <div className="absolute -right-10 -top-10 font-display font-black text-[22vw] leading-none opacity-[0.06] text-black pointer-events-none select-none">SHOP</div>
    <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <div className="font-overline text-xs mb-3">— ACQUISTA ONLINE</div>
        <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-none mb-4">I TUOI INTEGRATORI A CASA IN 2-4 GIORNI.</h2>
        <p className="text-base md:text-lg leading-relaxed max-w-xl mb-7 font-medium">Scegli i prodotti, inserisci l'indirizzo e paga come preferisci: carta, PayPal, Klarna a rate, bonifico o contrassegno. Nessuna registrazione obbligatoria, spedizione gratis sopra €39.</p>
        <Link to="/catalog" className="inline-flex items-center gap-2 bg-black text-white font-overline text-xs px-8 h-12 hover:bg-white hover:text-black transition-colors" data-testid="shop-online-cta">COMPRA ORA <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="lg:col-span-5 grid grid-cols-2 gap-3">
        {[["2-4 gg", "Consegna rapida in tutta Italia"], ["Gratis", "Spedizione sopra €39"], ["Sicuro", "Carta, PayPal, Klarna, contrassegno"], ["Ritiro", "Gratis a San Cataldo e Canicattì"]].map(([k, v]) => (
          <div key={k} className="border-2 border-black/80 p-4">
            <div className="font-display font-black text-2xl leading-none">{k}</div>
            <div className="text-xs mt-1 font-medium">{v}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
